import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Injectable} from '@angular/core';
import {map} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription} from 'rxjs';
import {MmOrderService} from './mm-order.service';
import {MmCraftService} from './mm-craft.service';
import {DynamicFormReadyEvent, FilterSelectFieldConfig, SelectItem} from '@mydocma/dynamic-form';
import {getCompanyInfo} from '@docma-mm-core/company/company';
import {TranslateService} from '@ngx-translate/core';
import {FaultField} from '../fault-field';
import {PersonalizationService} from '@docma-mm-core/personalization/personalization.service';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {MmViewService} from '../../../../mm-view.service';
import {MmCompanyDependencyService, NotificationSenderEnum} from './mm-company-dependency.service';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {FaultDependencyEnum} from './fault-dependency-enum';


@Injectable()
@UntilDestroy()
export class MmCompanyService {

  isNotificationSuspended: boolean = false;
  removeClearButton?: boolean = undefined;

  fault: FaultData;

  companyField: FaultField;
  companyFieldConfig :FilterSelectFieldConfig;
  formReadyEvent: DynamicFormReadyEvent;

  companies = new Observable<SelectItem[]>();
  allCompanies: SelectItem[];
  allCrafts: SelectItem[];
  allOrders: SelectItem[];
  deactivatedCompanies: SelectItem[];

  changedSubscriptions: Subscription[] = [];

  companyInfoMessageSubject = new BehaviorSubject<FaultDependencyEnum[]>(null);
  companyInfoMessage$ = this.companyInfoMessageSubject.asObservable();

  constructor(private readonly projectLoaderService: MmProjectLoaderService,
              private readonly companyDependencyService: MmCompanyDependencyService,
              private readonly orderService: MmOrderService,
              private readonly craftService: MmCraftService,
              private readonly personalizationService: PersonalizationService,
              private readonly mmViewService: MmViewService,
              private readonly translateService: TranslateService) {

    this.translateService.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe(lang => {
        this.companyInfoMessageSubject.next(this.companyInfoMessageSubject.value);
      });
  }

  preInitialize() {
    if (this.changedSubscriptions && (this.changedSubscriptions?.length ?? 0) > 0) {
      this.changedSubscriptions?.forEach(s => {
        s.unsubscribe();
      });
      this.changedSubscriptions = [];
    }
  }
  async initialize(fault: FaultData, companyField: FaultField, companyFieldConfig: FilterSelectFieldConfig, formReadyEvent: DynamicFormReadyEvent) {
    this.fault = fault;
    this.companyField = companyField;
    this.companyFieldConfig = companyFieldConfig;
    this.formReadyEvent = formReadyEvent;

    const result = await firstValueFrom(combineLatest([this.companyDependencyService.allCompanies,
                                                 this.companyDependencyService.allOrders,
                                                 this.companyDependencyService.allCrafts,
                                                 this.companyDependencyService.getDeactivatedCompanies(this.fault.Company?.Id),
                                                 this.companyDependencyService.selectedCompany$,
                                                 this.companyDependencyService.selectedOrder$,
                                                 this.companyDependencyService.selectedCraft$,
                                                 this.companyDependencyService.loadRecentlyUsedEntities(PersonalizationKeysEnum.RecentlyUsedCompanies)])
      .pipe(untilDestroyed(this),
        map(async ([allCompanies, allOrders, allCrafts, deactivatedCompanies, selectedCompany, selectedOrder, selectedCraft]) => {
          this.allCompanies = allCompanies;
          this.allCrafts = allCrafts;
          this.allOrders = allOrders;
          this.deactivatedCompanies = deactivatedCompanies;

          const result = await this.processDependencies(selectedOrder, selectedCraft, this.companyDependencyService.recentlyUsedCompanies, NotificationSenderEnum.initialize);
          return result;
        })
      ));

    this.companies = of(result.companies);
    await this.updateFieldConfig();

    // this.companyDependencyService.recentlyUsedCompanies$
    //   .pipe(untilDestroyed(this))
    //   .subscribe(async param => {
    //
    //   });

    this.changedSubscriptions.push(this.companyDependencyService.selectedCompany$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender === NotificationSenderEnum.company) {
          await this.setInfoMessage(param, false);
        }
      }));

    this.changedSubscriptions.push(this.companyDependencyService.selectedCraft$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender !== NotificationSenderEnum.initialize && companyFieldConfig) {
          const selectedCraft = param;
          const selectedOrder = await firstValueFrom(this.companyDependencyService.selectedOrder$);
          const result = await this.processDependencies(selectedOrder, selectedCraft, this.companyDependencyService.recentlyUsedCompanies, param.sender);
          this.companies = of(result.companies);
          await this.updateFieldConfig();

          const currentCompany = this.formReadyEvent.dynamicFormService.getFieldValue(companyFieldConfig.name);

          if (param.sender !== NotificationSenderEnum.company && result.selectCompany && result.selectCompany.id !== currentCompany && !currentCompany && selectedCraft.value) {
            this.formReadyEvent.dynamicFormService.updateFieldValue(companyFieldConfig.name, result.selectCompany.id, false);
            this.companyDependencyService.selectedCompanySubject.next({
              sender: param.sender,
              value: result.selectCompany
            });
            this.companyDependencyService.onFieldValueChanged.emit();
          }

          const onlyReset = (param.sender === NotificationSenderEnum.order || param.sender === NotificationSenderEnum.craft);
          await this.setInfoMessage(param, onlyReset);
        }
      }));

    this.changedSubscriptions.push(this.companyDependencyService.selectedOrder$
      .pipe(untilDestroyed(this))
      .subscribe(async param => {
        if (!this.isNotificationSuspended && param.sender !== NotificationSenderEnum.initialize && companyFieldConfig) {
          const selectedOrder = param;
          const selectedCraft = await firstValueFrom(this.companyDependencyService.selectedCraft$);
          const result = await this.processDependencies(selectedOrder, selectedCraft, this.companyDependencyService.recentlyUsedCompanies, param.sender);
          this.companies = of(result.companies);
          await this.updateFieldConfig();

          const currentCompany = this.formReadyEvent.dynamicFormService.getFieldValue(companyFieldConfig.name);

          if (param.sender !== NotificationSenderEnum.company && result.selectCompany && result.selectCompany.id !== currentCompany  && !currentCompany && selectedOrder.value) {
            this.formReadyEvent.dynamicFormService.updateFieldValue(companyFieldConfig.name, result.selectCompany.id, false);
            this.companyDependencyService.selectedCompanySubject.next({
              sender: param.sender,
              value: result.selectCompany
            });
            this.companyDependencyService.onFieldValueChanged.emit();
          }

          const onlyReset = (param.sender === NotificationSenderEnum.order || param.sender === NotificationSenderEnum.craft);
          await this.setInfoMessage(param, onlyReset);
        }
      }));
  }

  async updateFieldConfig() {
    if (this.companyFieldConfig) {
      this.companyFieldConfig.groupedSource = true;
      this.companyFieldConfig.removeClearButton = (this.removeClearButton ? this.removeClearButton : this.companyFieldConfig.readonly);
      this.companyFieldConfig.source = await firstValueFrom(this.companies);
      this.formReadyEvent.dynamicFormService.updateFieldConfig(this.companyFieldConfig);
    }
  }

  async processDependencies(selectedOrder: {value: any, sender: NotificationSenderEnum}, selectedCraft: {value: any, sender: NotificationSenderEnum}, recentlyUsedCompanies: { [index: number]: number }, sender: NotificationSenderEnum) {
    const groupItems: {[id: number]: SelectItem} = [];
    let selectCompany: SelectItem;
    const allCompanies = (this.deactivatedCompanies ?? []).concat(this.allCompanies);

    const order = (selectedOrder?.value?.id ? this.allOrders.find(x => x.id === selectedOrder.value.id) : null);
    const craft = (selectedCraft?.value?.id ? this.allCrafts.find(x => x.id === selectedCraft.value.id) : null);

    if (order && craft) {
      if (order.data.Craft?.Id === craft.id) {
        // FILTERED + LAST USED + ALL
        selectCompany = this.addOrderCraftCompanies(allCompanies, order, craft, groupItems);
        this.companyDependencyService.addRecentlyUsedEntities(allCompanies, recentlyUsedCompanies, groupItems);
        this.addAllCompanies(allCompanies, this.allOrders, groupItems, order, craft);

        // MESSAGE #2
      }
      else {
        // LAST USED + ALL
        this.companyDependencyService.addRecentlyUsedEntities(allCompanies, recentlyUsedCompanies, groupItems);
        selectCompany = this.addAllCompanies(allCompanies, this.allOrders, groupItems, order, craft, true);

        // MESSAGE #1
      }
    }
    else if (order && !craft) {
      // FILTERED + LAST USED + ALL
      selectCompany = this.addOrderCompanies(allCompanies, order, groupItems);
      this.companyDependencyService.addRecentlyUsedEntities(allCompanies, recentlyUsedCompanies, groupItems);
      this.addAllCompanies(allCompanies, this.allOrders, groupItems, order, craft);

      // MESSAGE #2
    }
    else if (!order && craft) {
      selectCompany = this.addCraftCompanies(allCompanies, this.allOrders, craft, groupItems);
      this.companyDependencyService.addRecentlyUsedEntities(allCompanies, recentlyUsedCompanies, groupItems);
      this.addAllCompanies(allCompanies, this.allOrders, groupItems, order, craft);

      // MESSAGE #1
    }
    else {
      // LAST USED + ALL
      this.companyDependencyService.addRecentlyUsedEntities(allCompanies, recentlyUsedCompanies, groupItems);
      selectCompany = this.addAllCompanies(allCompanies, this.allOrders, groupItems, order, craft, true);
    }

    const items = (groupItems && Object.keys(groupItems).length > 0 ? Object.values(groupItems) : allCompanies);
    return {companies: items, selectCompany: selectCompany};
  }

  addOrderCompanies(allCompanies: SelectItem[], order: SelectItem, groupItems: {[id: number]: SelectItem}) {
    const items: SelectItem[] = allCompanies.filter(x => x.id === order.data.Company.Id);

    if ((items?.length ?? 0) > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup1').replace('ENTITY', order.text);
      groupItems[0] = {id: 0, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addOrderCraftCompanies(allCompanies: SelectItem[], order: SelectItem, craft: SelectItem, groupItems: {[id: number]: SelectItem}) {
    const items: SelectItem[] = allCompanies.filter(x => x.id === order.data.Company.Id);

    if ((items?.length ?? 0) > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup2').replace('ENTITY', order.text).replace('ENTITY2', craft.text);
      groupItems[0] = {id: 1, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addCraftCompanies(allCompanies: SelectItem[], allOrders: SelectItem[], craft: SelectItem, groupItems: {[id: number]: SelectItem}) {
    const items = allOrders.filter(o => o.data?.Craft && o.data.Craft.Id === craft.id)
      .map(c => ({id: c.data.Company.Id, text: (c.data.Company.ShortName + (c.data.Company.Name1 ? ' - ' + c.data.Company.Name1 : '')) + getCompanyInfo(c.data.Company, this.translateService)}))
      .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []);

    if ((items?.length ?? 0) > 0) {  // IF ORDERS: FILTERED + LAST USED + ALL
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyForGoup1').replace('ENTITY', craft.text);
      groupItems[0] = {id: 0, text: nameFunc(), data: nameFunc};
      groupItems[0].items = items;
    }

    if ((items?.length ?? 0) === 1) {
      return items[0];
    }
    return null;
  }

  addAllCompanies(allCompanies: SelectItem[], allOrders: SelectItem[], groupItems: {[id: number]: SelectItem}, order: SelectItem, craft: SelectItem, returnSelectedCompany: boolean = false) {
    let selectedItem: SelectItem;

    if (returnSelectedCompany) {
      if (order?.data?.Company?.Id) {
        selectedItem = allCompanies.find(x => x.id === order.data.Company.Id);
      }
      else if (craft) {
        const itemsByCraft = allOrders.filter(o => o.data?.Craft && o.data.Craft.Id === craft.id)
          .map(c => ({
            id: c.data.Company.Id,
            text: (c.data.Company.ShortName + (c.data.Company.Name1 ? ' - ' + c.data.Company.Name1 : '')) + getCompanyInfo(c.data.Company, this.translateService)
          }))
          .reduce((arr: SelectItem[], item: SelectItem) => arr.find(p => p.id === item.id) ? arr : [...arr, item], []);

        if ((itemsByCraft?.length ?? 0) === 1) {
          selectedItem = itemsByCraft[0];
        }
      }
    }

    if (groupItems && Object.keys(groupItems).length > 0) {
      const nameFunc = () => this.translateService.instant('MM.fault.dependencyAllGoup');
      groupItems[2] = {id: 2, text: nameFunc(), data: nameFunc};
      groupItems[2].items = allCompanies;
    }

    if (returnSelectedCompany) {
      return selectedItem;
    }
  }

  async setInfoMessage(param: {value: SelectItem, sender: NotificationSenderEnum}, onlyReset: boolean) {
    if (param.sender !== NotificationSenderEnum.initialize) {
      let infoMessage: FaultDependencyEnum[] = null;

      const selectedCompany = await firstValueFrom(this.companyDependencyService.selectedCompany$);

      const selectedCraft = await firstValueFrom(this.companyDependencyService.selectedCraft$);
      const selectedOrder = await firstValueFrom(this.companyDependencyService.selectedOrder$);

      const company = (selectedCompany?.value?.id ? this.allCompanies.find(x => x.id === selectedCompany.value.id) : null);
      const order = (selectedOrder?.value?.id ? this.allOrders.find(x => x.id === selectedOrder.value.id) : null);
      const craft = (selectedCraft?.value?.id ? this.allCrafts.find(x => x.id === selectedCraft.value.id) : null);

      if (order && craft) {
        if (order.data.Craft?.Id === craft.id) {
          if (company && order.data?.Company?.Id !== company.id) {
            // console.warn('COMPANY.MESSAGE #1');
            infoMessage = [FaultDependencyEnum.Order, FaultDependencyEnum.Craft];
          }
        }
        else {
          if (company && craft.data?.Company?.Id !== company.id) {
            // console.warn('COMPANY.MESSAGE #1');
            infoMessage = [FaultDependencyEnum.Order, FaultDependencyEnum.Craft];
          }
        }
      }
      else if (order && !craft) {
        if (company && order.data?.Company?.Id !== company.id) {
          // console.warn('COMPANY.MESSAGE #2');
          infoMessage = [FaultDependencyEnum.Order];
        }
      }
      else if (!order && craft) {
        const items = this.allOrders.filter(o => o.data?.Craft && o.data.Craft.Id === craft.id)
          .map(c => ({id: c.data?.Company?.Id, text: (c.data?.Company?.Name1 + (c.data?.Company?.Name2 ? ' - ' + c.data?.Company.Name2 : '')), data: c.data}))
          .reduce((arr: SelectItem[], item: SelectItem) =>  arr.find(p => p.id === item.id ) ? arr : [...arr, item], []);

        if ((items?.length ?? 0) >= 1) {
          if (company && !items.some(x => x.data?.Company?.Id === company.id)) {
            // console.warn('COMPANY.MESSAGE #1');
            infoMessage = [FaultDependencyEnum.Craft];
          }
        }
        else if ((items?.length ?? 0) === 0 && company) {
          // console.warn('COMPANY.MESSAGE #1');
          infoMessage = [FaultDependencyEnum.Craft];
        }
      }
      else {
        // NO MESSAGE!
      }

      if (!onlyReset || (onlyReset && !infoMessage)) {
        // console.warn('COMPANY.MESSAGE ' + infoMessage);
        this.companyInfoMessageSubject.next(infoMessage);
      }
    }
  }

  async reloadCompanies() {
    const selectedCraft = await firstValueFrom(this.companyDependencyService.selectedCraft$);
    const selectedOrder = await firstValueFrom(this.companyDependencyService.selectedOrder$);
    const result = await this.processDependencies(selectedOrder, selectedCraft, this.companyDependencyService.recentlyUsedCompanies, NotificationSenderEnum.initialize);
    this.companies = of(result.companies);
    await this.updateFieldConfig();
  }
}
